import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useClassifiersConfig } from '../contexts/ClassifiersConfigContext';
import {
  adminApi,
  BulkActionResult,
  Classifier,
  ClassifierCreatePayload,
  ClassifierUpdatePayload,
  ClassifierCardinality,
  ClassifierValueSource,
  TaxonomyEntry,
  TaxonomyEntryCreatePayload,
  TaxonomyEntryUpdatePayload,
  TaxonomyTreeNode,
  TaxonomyLink,
  TaxonomyLinkType,
  PendingFacet,
  PendingFacetStatus,
  PendingFacetPromotePayload,
} from '../api/admin';
import AdminTabsNav from '../components/AdminTabsNav';
import Modal from '../components/Modal';
import { useIsMobileViewport } from '../utils/useIsMobileViewport';
import './AdminConfig.css';
import './AdminUsers.css';
import './AdminClassifications.css';

// ---------------------------------------------------------------------------
// Page shell
//
// Layout matches AdminUsers: breadcrumbs -> AdminTabsNav -> hero with title
// + subtitle + "+ Adauga" -> flash region -> sub-tab content. The three
// sub-tabs (Clasificatori / Valori si legaturi / Netriajate) are rendered
// inline below the hero so the user can flip between them without
// changing the URL — the page itself is one cohesive admin surface.
// ---------------------------------------------------------------------------

type SubTab = 'classifiers' | 'tree' | 'pending';

type ApiError = { response?: { data?: { detail?: string } } };

const extractError = (e: unknown, fallback: string): string => {
  const err = e as ApiError;
  return err.response?.data?.detail || fallback;
};

const KEY_REGEX = /^[a-z][a-z0-9_]*$/;
// Accepts `a`, `a.b`, `a[].b`, `a.b[].c`, etc.; each segment is a slug
// optionally followed by `[]` for "array of objects".
const JSON_PATH_REGEX = /^[a-z][a-z0-9_]*(\[\])?(\.[a-z][a-z0-9_]*(\[\])?)*$/;

const isValidKey = (k: string) => KEY_REGEX.test(k.trim());
const isValidJsonPath = (p: string) => JSON_PATH_REGEX.test(p.trim());

const labelFor = (id: number, tree: TaxonomyTreeNode[]): string => {
  // Walk the tree to find the entry and return "Parent > Child" path.
  const path: string[] = [];
  const visit = (nodes: TaxonomyTreeNode[], trail: string[]): boolean => {
    for (const n of nodes) {
      const next = [...trail, n.label_ro];
      if (n.id === id) {
        path.push(...next);
        return true;
      }
      if (visit(n.children, next)) return true;
    }
    return false;
  };
  visit(tree, []);
  return path.length ? path.join(' › ') : '—';
};

interface FlatTreeOption {
  id: number;
  label: string;
  depth: number;
}

const flattenTree = (
  nodes: TaxonomyTreeNode[],
  depth = 0,
  acc: FlatTreeOption[] = [],
  trail: string[] = []
): FlatTreeOption[] => {
  for (const n of nodes) {
    const next = [...trail, n.label_ro];
    acc.push({ id: n.id, label: next.join(' › '), depth });
    flattenTree(n.children, depth + 1, acc, next);
  }
  return acc;
};

// ---------------------------------------------------------------------------
// Top-level page
// ---------------------------------------------------------------------------

const AdminClassifications: React.FC = () => {
  const { user } = useAuth();
  const { refresh: refreshConfig } = useClassifiersConfig();

  const [activeTab, setActiveTab] = useState<SubTab>('classifiers');
  const [classifiers, setClassifiers] = useState<Classifier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [selectedClassifierId, setSelectedClassifierId] = useState<number | null>(null);
  const [editor, setEditor] = useState<{ mode: 'create' | 'edit'; classifier?: Classifier } | null>(
    null
  );
  const [pendingFilter, setPendingFilter] = useState<{
    classifier_id: number | 'all';
    status: PendingFacetStatus | 'all';
  }>({ classifier_id: 'all', status: 'pending' });

  const loadList = useCallback(async () => {
    const list = await adminApi.classifiers.list();
    setClassifiers(list);
    return list;
  }, []);

  useEffect(() => {
    if (!user?.is_admin) return;
    (async () => {
      try {
        await loadList();
      } catch (e) {
        setError(extractError(e, 'Nu s-au putut încărca clasificatorii'));
      } finally {
        setLoading(false);
      }
    })();
  }, [user, loadList]);

  // Auto-dismiss the success flash so it doesn't pile up across mutations.
  useEffect(() => {
    if (!info) return;
    const t = window.setTimeout(() => setInfo(''), 4000);
    return () => window.clearTimeout(t);
  }, [info]);

  const handleAfterMutation = useCallback(
    async (msg: string) => {
      setInfo(msg);
      setError('');
      try {
        await loadList();
        // Best-effort: keep the runtime config in sync so filters /
        // chip sections elsewhere see the change without a hard reload.
        await refreshConfig();
      } catch (e) {
        // The mutation itself succeeded; the refresh is best-effort.
        setError(extractError(e, 'Eroare la reîncărcare'));
      }
    },
    [loadList, refreshConfig]
  );

  const pendingTotal = useMemo(
    () => classifiers.reduce((acc, c) => acc + (c.pending_count || 0), 0),
    [classifiers]
  );

  if (!user?.is_admin) {
    return (
      <div className="page-container">
        <p className="alert alert-error">Acces interzis.</p>
        <Link to="/dashboard">Înapoi</Link>
      </div>
    );
  }

  return (
    <div className="page-container admin-page">
      <nav className="breadcrumbs admin-breadcrumbs">
        <Link to="/dashboard">Dashboard</Link>
        <span className="admin-bc-sep">/</span>
        <span>Administrare</span>
        <span className="admin-bc-sep">/</span>
        <span>Clasificări</span>
      </nav>

      <AdminTabsNav />

      <header className="admin-hero">
        <div className="admin-hero-text">
          <h1 className="page-title admin-hero-title">Clasificări</h1>
          <p className="page-subtitle admin-hero-sub">
            Definește categoriile de clasificare a documentelor, arborele de valori și ce ajunge în
            filtre și fișa documentului. Modificările intră în vigoare la următoarea clasificare AI.
          </p>
        </div>
        <div className="admin-hero-actions">
          {activeTab === 'classifiers' && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setEditor({ mode: 'create' })}
            >
              + Clasificator nou
            </button>
          )}
        </div>
      </header>

      {error && (
        <div className="admin-flash admin-flash--error" role="alert">
          <span className="admin-flash__text">{error}</span>
        </div>
      )}
      {info && (
        <div className="admin-flash admin-flash--success" role="status">
          <span className="admin-flash__text">{info}</span>
        </div>
      )}

      <div className="tabs-bar adm-cls-subtabs" role="tablist" aria-label="Secțiuni clasificări">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'classifiers'}
          className={`tabs-bar__tab adm-cls-subtab${activeTab === 'classifiers' ? ' adm-cls-subtab--active' : ''}`}
          onClick={() => setActiveTab('classifiers')}
        >
          Clasificatori
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'tree'}
          className={`tabs-bar__tab adm-cls-subtab${activeTab === 'tree' ? ' adm-cls-subtab--active' : ''}`}
          onClick={() => setActiveTab('tree')}
        >
          Valori și legături
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'pending'}
          className={`tabs-bar__tab adm-cls-subtab${activeTab === 'pending' ? ' adm-cls-subtab--active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          Netriajate
          {pendingTotal > 0 && <span className="tabs-bar__count adm-cls-subtab__badge">{pendingTotal}</span>}
        </button>
      </div>

      {loading ? (
        <div className="admin-skeleton-grid">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="card admin-skeleton-card" />
          ))}
        </div>
      ) : (
        <>
          {activeTab === 'classifiers' && (
            <ClassifiersGrid
              classifiers={classifiers}
              onEdit={(c) => setEditor({ mode: 'edit', classifier: c })}
              onAfterMutation={handleAfterMutation}
              onError={setError}
              onShowEntries={(id) => {
                setSelectedClassifierId(id);
                setActiveTab('tree');
              }}
            />
          )}
          {activeTab === 'tree' && (
            <TaxonomyEditor
              classifiers={classifiers}
              selectedClassifierId={selectedClassifierId}
              onSelectClassifier={setSelectedClassifierId}
              onAfterMutation={handleAfterMutation}
              onError={setError}
            />
          )}
          {activeTab === 'pending' && (
            <PendingInbox
              classifiers={classifiers}
              filter={pendingFilter}
              onFilterChange={setPendingFilter}
              onAfterMutation={handleAfterMutation}
              onError={setError}
            />
          )}
        </>
      )}

      <ClassifierEditorModal
        state={editor}
        onClose={() => setEditor(null)}
        onSaved={async (msg) => {
          setEditor(null);
          await handleAfterMutation(msg);
        }}
        onError={setError}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-tab: Clasificatori (card grid + kebab actions)
// ---------------------------------------------------------------------------

interface ClassifiersGridProps {
  classifiers: Classifier[];
  onEdit: (c: Classifier) => void;
  onAfterMutation: (msg: string) => Promise<void>;
  onError: (msg: string) => void;
  onShowEntries: (id: number) => void;
}

const ClassifiersGrid: React.FC<ClassifiersGridProps> = ({
  classifiers,
  onEdit,
  onAfterMutation,
  onError,
  onShowEntries,
}) => {
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  const handleDelete = async (c: Classifier) => {
    const ok = window.confirm(
      `Șterge clasificatorul „${c.label_ro}”?\n\n` +
        'Toate valorile din arbore, legăturile, valorile netriajate ȘI etichetele aplicate documentelor pentru această categorie vor fi șterse definitiv.'
    );
    if (!ok) return;
    try {
      const res = await adminApi.classifiers.remove(c.id);
      await onAfterMutation(
        `Clasificator șters: ${c.label_ro} (${res.cascaded_facets} etichete documente, ${res.cascaded_entries} valori arbore, ${res.cascaded_pending} netriajate șterse).`
      );
    } catch (e) {
      onError(extractError(e, 'Eroare la ștergerea clasificatorului'));
    }
  };

  if (classifiers.length === 0) {
    return (
      <div className="card adm-cls-empty">
        Niciun clasificator definit. Apasă „+ Clasificator nou” pentru a crea primul.
      </div>
    );
  }

  return (
    <div className="admin-grid">
      {classifiers.map((c) => (
        <div
          key={c.id}
          className={`adm-cls-card${!c.active ? ' adm-cls-card--inactive' : ''}`}
        >
          <div className="adm-cls-card__head">
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 className="adm-cls-card__title">{c.label_ro}</h3>
              <span className="adm-cls-card__key">{c.key}</span>
            </div>
            <RowActionsMenu
              open={openMenuId === c.id}
              onOpen={() => setOpenMenuId(c.id)}
              onClose={() => setOpenMenuId(null)}
              items={[
                {
                  label: 'Editează',
                  onClick: () => {
                    setOpenMenuId(null);
                    onEdit(c);
                  },
                },
                {
                  label: 'Vezi valori',
                  onClick: () => {
                    setOpenMenuId(null);
                    onShowEntries(c.id);
                  },
                },
                { type: 'separator' },
                {
                  label: 'Șterge',
                  danger: true,
                  onClick: () => {
                    setOpenMenuId(null);
                    void handleDelete(c);
                  },
                },
              ]}
              ariaLabel={`Acțiuni clasificator ${c.label_ro}`}
            />
          </div>

          <span className="adm-cls-card__path" title={c.json_path}>
            {c.json_path}
          </span>

          <div className="adm-cls-card__pills">
            <span className="adm-cls-pill adm-cls-pill--cardinality">
              {c.cardinality === 'multi' ? 'multi-valoare' : 'singur'}
            </span>
            <span className="adm-cls-pill adm-cls-pill--source">{c.value_source}</span>
            {!c.active && <span className="adm-cls-pill adm-cls-pill--inactive">inactiv</span>}
          </div>

          <div className="adm-cls-card__flags">
            <span className={`adm-cls-flag${c.show_in_filters ? ' adm-cls-flag--on' : ''}`}>
              <span className="adm-cls-flag-dot" /> filtre
            </span>
            <span className={`adm-cls-flag${c.show_in_card ? ' adm-cls-flag--on' : ''}`}>
              <span className="adm-cls-flag-dot" /> card
            </span>
            <span className={`adm-cls-flag${c.show_in_detail ? ' adm-cls-flag--on' : ''}`}>
              <span className="adm-cls-flag-dot" /> detaliu
            </span>
          </div>

          <div className="adm-cls-card__counts">
            <span className="adm-cls-count">{c.entry_count} valori</span>
            {c.pending_count > 0 && (
              <span className="adm-cls-count adm-cls-count--warn">
                {c.pending_count} netriajate
              </span>
            )}
            {c.description && (
              <span className="adm-cls-count" title={c.description} style={{ maxWidth: '100%' }}>
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 220,
                    display: 'inline-block',
                    verticalAlign: 'bottom',
                  }}
                >
                  {c.description}
                </span>
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-tab: Valori și legături (3-column layout: picker | tree | links)
// ---------------------------------------------------------------------------

interface TaxonomyEditorProps {
  classifiers: Classifier[];
  selectedClassifierId: number | null;
  onSelectClassifier: (id: number) => void;
  onAfterMutation: (msg: string) => Promise<void>;
  onError: (msg: string) => void;
}

const TaxonomyEditor: React.FC<TaxonomyEditorProps> = ({
  classifiers,
  selectedClassifierId,
  onSelectClassifier,
  onAfterMutation,
  onError,
}) => {
  const [tree, setTree] = useState<TaxonomyTreeNode[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState<number | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [entryEditor, setEntryEditor] = useState<
    { mode: 'create' | 'edit'; entry?: TaxonomyTreeNode; parentId?: number | null } | null
  >(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [outgoingLinks, setOutgoingLinks] = useState<TaxonomyLink[]>([]);
  const [linksLoading, setLinksLoading] = useState(false);

  // Auto-select the first classifier if none is selected yet — saves an
  // extra click when landing on the Valori tab from the kebab menu.
  useEffect(() => {
    if (selectedClassifierId == null && classifiers.length > 0) {
      onSelectClassifier(classifiers[0].id);
    }
  }, [classifiers, selectedClassifierId, onSelectClassifier]);

  const loadTree = useCallback(async () => {
    if (selectedClassifierId == null) return;
    setTreeLoading(true);
    try {
      const t = await adminApi.classifiers.getTree(selectedClassifierId);
      setTree(t);
    } catch (e) {
      onError(extractError(e, 'Eroare la încărcarea arborelui'));
    } finally {
      setTreeLoading(false);
    }
  }, [selectedClassifierId, onError]);

  useEffect(() => {
    setSelectedNodeId(null);
    setExpanded(new Set());
    setOutgoingLinks([]);
    void loadTree();
  }, [selectedClassifierId, loadTree]);

  const loadLinks = useCallback(async (entryId: number) => {
    setLinksLoading(true);
    try {
      const links = await adminApi.classifiers.listLinks(entryId);
      setOutgoingLinks(links);
    } catch (e) {
      onError(extractError(e, 'Eroare la încărcarea legăturilor'));
    } finally {
      setLinksLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    if (selectedNodeId == null) {
      setOutgoingLinks([]);
      return;
    }
    void loadLinks(selectedNodeId);
  }, [selectedNodeId, loadLinks]);

  const handleDeleteEntry = async (entry: TaxonomyTreeNode) => {
    const ok = window.confirm(
      `Șterge valoarea „${entry.label_ro}”?\n\nCopiii vor fi promovați la nivelul curent.`
    );
    if (!ok) return;
    try {
      await adminApi.classifiers.removeEntry(entry.id);
      await loadTree();
      if (selectedNodeId === entry.id) setSelectedNodeId(null);
      await onAfterMutation(`Valoare ștearsă: ${entry.label_ro}`);
    } catch (e) {
      onError(extractError(e, 'Eroare la ștergerea valorii'));
    }
  };

  const handleRemoveLink = async (link: TaxonomyLink) => {
    try {
      await adminApi.classifiers.removeLink(link.id);
      await loadLinks(selectedNodeId!);
      await onAfterMutation('Legătură ștearsă');
    } catch (e) {
      onError(extractError(e, 'Eroare la ștergerea legăturii'));
    }
  };

  const toggleExpand = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedClassifier = classifiers.find((c) => c.id === selectedClassifierId) || null;

  return (
    <>
      <div className="adm-cls-tree-layout">
        {/* Left column: classifier picker */}
        <aside className="adm-cls-tree-panel" aria-label="Clasificatori">
          <div className="adm-cls-tree-panel__head">
            <h3 className="adm-cls-tree-panel__title">Clasificatori</h3>
          </div>
          <div className="adm-cls-tree-panel__body">
            {classifiers.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`adm-cls-picker-item${
                  c.id === selectedClassifierId ? ' adm-cls-picker-item--active' : ''
                }`}
                onClick={() => onSelectClassifier(c.id)}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.label_ro}
                </span>
                <span className="adm-cls-picker-item__count">{c.entry_count}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Middle column: tree */}
        <section className="adm-cls-tree-panel" aria-label="Arborele de valori">
          <div className="adm-cls-tree-panel__head">
            <h3 className="adm-cls-tree-panel__title">
              {selectedClassifier ? `Valori: ${selectedClassifier.label_ro}` : 'Valori'}
            </h3>
            {selectedClassifier && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setEntryEditor({ mode: 'create', parentId: null })}
              >
                + Valoare rădăcină
              </button>
            )}
          </div>
          <div className="adm-cls-tree-panel__body">
            {!selectedClassifier ? (
              <div className="adm-cls-tree-empty">Selectează un clasificator.</div>
            ) : treeLoading ? (
              <div className="adm-cls-tree-empty">Se încarcă…</div>
            ) : tree.length === 0 ? (
              <div className="adm-cls-tree-empty">
                Niciun nod în arbore. Apasă „+ Valoare rădăcină” pentru a crea primul.
              </div>
            ) : (
              <TreeBody
                nodes={tree}
                expanded={expanded}
                selectedId={selectedNodeId}
                openMenuId={openMenuId}
                onToggle={toggleExpand}
                onSelect={setSelectedNodeId}
                onOpenMenu={setOpenMenuId}
                onAddChild={(parentId) => {
                  setOpenMenuId(null);
                  setEntryEditor({ mode: 'create', parentId });
                }}
                onEdit={(n) => {
                  setOpenMenuId(null);
                  setEntryEditor({ mode: 'edit', entry: n });
                }}
                onDelete={(n) => {
                  setOpenMenuId(null);
                  void handleDeleteEntry(n);
                }}
              />
            )}
          </div>
        </section>

        {/* Right column: outgoing links for the selected node */}
        <aside className="adm-cls-tree-panel" aria-label="Legături">
          <div className="adm-cls-tree-panel__head">
            <h3 className="adm-cls-tree-panel__title">Legături</h3>
            {selectedNodeId != null && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => setLinkModalOpen(true)}
              >
                + Adaugă
              </button>
            )}
          </div>
          <div className="adm-cls-tree-panel__body adm-cls-tree-panel__body--padded">
            {selectedNodeId == null ? (
              <div className="adm-cls-tree-empty">
                Selectează o valoare pentru a vedea legăturile spre alte clasificări.
              </div>
            ) : linksLoading ? (
              <div className="adm-cls-tree-empty">Se încarcă…</div>
            ) : outgoingLinks.length === 0 ? (
              <div className="adm-cls-tree-empty">Nicio legătură definită.</div>
            ) : (
              outgoingLinks.map((link) => (
                <div key={link.id} className="adm-cls-link-row">
                  <div className="adm-cls-link-row__text">
                    <div className="adm-cls-link-row__cls">
                      {link.to_classifier?.label_ro || `Clasificator #${link.to_entry?.classifier_id ?? '?'}`}
                    </div>
                    <div className="adm-cls-link-row__label">
                      {link.to_entry?.label_ro || `Valoare #${link.to_entry_id}`}
                    </div>
                    <span className="adm-cls-link-row__type">{link.link_type}</span>
                  </div>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm btn-icon"
                    aria-label="Șterge legătura"
                    onClick={() => void handleRemoveLink(link)}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              ))
            )}
          </div>
        </aside>
      </div>

      <TaxonomyEntryEditorModal
        classifier={selectedClassifier}
        tree={tree}
        state={entryEditor}
        onClose={() => setEntryEditor(null)}
        onSaved={async (msg) => {
          setEntryEditor(null);
          await loadTree();
          await onAfterMutation(msg);
        }}
        onError={onError}
      />

      <LinkAddModal
        open={linkModalOpen && selectedNodeId != null}
        sourceEntryId={selectedNodeId}
        classifiers={classifiers}
        excludeClassifierId={selectedClassifierId}
        onClose={() => setLinkModalOpen(false)}
        onSaved={async (msg) => {
          setLinkModalOpen(false);
          if (selectedNodeId != null) await loadLinks(selectedNodeId);
          await onAfterMutation(msg);
        }}
        onError={onError}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Tree body — recursive renderer with per-node kebab.
// ---------------------------------------------------------------------------

interface TreeBodyProps {
  nodes: TaxonomyTreeNode[];
  expanded: Set<number>;
  selectedId: number | null;
  openMenuId: number | null;
  onToggle: (id: number) => void;
  onSelect: (id: number) => void;
  onOpenMenu: (id: number | null) => void;
  onAddChild: (parentId: number) => void;
  onEdit: (n: TaxonomyTreeNode) => void;
  onDelete: (n: TaxonomyTreeNode) => void;
  depth?: number;
}

const TreeBody: React.FC<TreeBodyProps> = ({
  nodes,
  expanded,
  selectedId,
  openMenuId,
  onToggle,
  onSelect,
  onOpenMenu,
  onAddChild,
  onEdit,
  onDelete,
  depth = 0,
}) => (
  <ul className={`adm-cls-tree${depth > 0 ? ' adm-cls-tree--child' : ''}`}>
    {nodes.map((n) => {
      const isOpen = expanded.has(n.id);
      const hasChildren = n.children.length > 0;
      return (
        <li key={n.id} className="adm-cls-tree-node">
          <div
            className={`adm-cls-tree-node__row${
              selectedId === n.id ? ' adm-cls-tree-node__row--selected' : ''
            }`}
            onClick={() => onSelect(n.id)}
          >
            {hasChildren ? (
              <button
                type="button"
                className="adm-cls-tree-node__chev"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(n.id);
                }}
                aria-label={isOpen ? 'Colapsează' : 'Expandează'}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 120ms ease' }}
                  aria-hidden
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            ) : (
              <span className="adm-cls-tree-node__chev adm-cls-tree-node__chev--placeholder" />
            )}
            <span className="adm-cls-tree-node__label">{n.label_ro}</span>
            <span className="adm-cls-tree-node__code">{n.code}</span>
            <RowActionsMenu
              open={openMenuId === n.id}
              onOpen={() => onOpenMenu(n.id)}
              onClose={() => onOpenMenu(null)}
              ariaLabel={`Acțiuni valoare ${n.label_ro}`}
              items={[
                {
                  label: 'Adaugă copil',
                  onClick: () => onAddChild(n.id),
                },
                { label: 'Editează', onClick: () => onEdit(n) },
                { type: 'separator' },
                { label: 'Șterge', danger: true, onClick: () => onDelete(n) },
              ]}
            />
          </div>
          {hasChildren && isOpen && (
            <TreeBody
              nodes={n.children}
              expanded={expanded}
              selectedId={selectedId}
              openMenuId={openMenuId}
              onToggle={onToggle}
              onSelect={onSelect}
              onOpenMenu={onOpenMenu}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onDelete={onDelete}
              depth={depth + 1}
            />
          )}
        </li>
      );
    })}
  </ul>
);

// ---------------------------------------------------------------------------
// Sub-tab: Netriajate (pending values table)
// ---------------------------------------------------------------------------

interface PendingInboxProps {
  classifiers: Classifier[];
  filter: { classifier_id: number | 'all'; status: PendingFacetStatus | 'all' };
  onFilterChange: (f: { classifier_id: number | 'all'; status: PendingFacetStatus | 'all' }) => void;
  onAfterMutation: (msg: string) => Promise<void>;
  onError: (msg: string) => void;
}

type BulkAction = 'promote' | 'merge' | 'ignore';

// Past-tense Romanian verb per action, used to phrase the result banner
// ("6/8 promovate, 2 omise"). Kept colocated with the action union so a
// new bulk variant must extend both at once.
const BULK_ACTION_PAST: Record<BulkAction, string> = {
  promote: 'promovate',
  merge: 'fuzionate',
  ignore: 'ignorate',
};

interface BulkResultState {
  action: BulkAction;
  total: number;
  result: BulkActionResult;
}

const PendingInbox: React.FC<PendingInboxProps> = ({
  classifiers,
  filter,
  onFilterChange,
  onAfterMutation,
  onError,
}) => {
  const [rows, setRows] = useState<PendingFacet[]>([]);
  const [loading, setLoading] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [promoteState, setPromoteState] = useState<{ pending: PendingFacet } | null>(null);
  const [mergeState, setMergeState] = useState<{ pending: PendingFacet } | null>(null);

  // Selection lives at the inbox level — set of pending row IDs. "Select
  // all visible" only ever touches `rows` (post-filter), never the full
  // pending table on the server, so the user can't accidentally bulk-
  // ignore rows they can't see.
  const [selection, setSelection] = useState<Set<number>>(() => new Set());
  const [bulkPromoteOpen, setBulkPromoteOpen] = useState(false);
  const [bulkMergeOpen, setBulkMergeOpen] = useState(false);
  const [bulkIgnoreOpen, setBulkIgnoreOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkResultState | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await adminApi.classifiers.listPending({
        classifier_id: filter.classifier_id === 'all' ? undefined : filter.classifier_id,
        status: filter.status,
      });
      list.sort((a, b) => b.occurrences_count - a.occurrences_count);
      setRows(list);
    } catch (e) {
      onError(extractError(e, 'Eroare la încărcarea valorilor netriajate'));
    } finally {
      setLoading(false);
    }
  }, [filter, onError]);

  useEffect(() => {
    void load();
  }, [load]);

  // Drop the selection whenever the filter changes — the visible row set
  // is different now, and keeping stale IDs would re-select silently if
  // the admin flips back. Bulk action success paths clear the selection
  // explicitly (see `handleBulkDone`).
  useEffect(() => {
    setSelection(new Set());
    setBulkResult(null);
  }, [filter.classifier_id, filter.status]);

  const classifierLabel = useCallback(
    (id: number): string => classifiers.find((c) => c.id === id)?.label_ro || `#${id}`,
    [classifiers]
  );

  // O(N) scan over the visible rows. The toolbar uses the selected subset
  // to decide button enablement; everything downstream (modals, API calls)
  // gets the materialized row list rather than just IDs so it can show
  // previews without re-querying the table.
  const visibleSelectedRows = useMemo(
    () => rows.filter((r) => selection.has(r.id)),
    [rows, selection]
  );
  const visibleSelectedIds = useMemo(
    () => visibleSelectedRows.map((r) => r.id),
    [visibleSelectedRows]
  );
  const selectedClassifierIds = useMemo(() => {
    const s = new Set<number>();
    for (const r of visibleSelectedRows) s.add(r.classifier_id);
    return s;
  }, [visibleSelectedRows]);
  const spansMultipleClassifiers = selectedClassifierIds.size > 1;
  const commonClassifierId =
    selectedClassifierIds.size === 1 ? Array.from(selectedClassifierIds)[0]! : null;

  // Header checkbox: tri-state via the DOM-only `indeterminate` flag.
  // React doesn't expose it as a prop, so we mirror it through a ref.
  const headerCheckboxRef = useRef<HTMLInputElement | null>(null);
  const visibleSelectedCount = visibleSelectedRows.length;
  const allVisibleSelected = rows.length > 0 && visibleSelectedCount === rows.length;
  const partialVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = partialVisibleSelected;
    }
  }, [partialVisibleSelected]);

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      // De-select only the visible rows; preserve any (hypothetical)
      // out-of-view selections — though in practice the filter-change
      // effect above clears them too.
      setSelection((prev) => {
        const next = new Set(prev);
        for (const r of rows) next.delete(r.id);
        return next;
      });
    } else {
      setSelection((prev) => {
        const next = new Set(prev);
        for (const r of rows) next.add(r.id);
        return next;
      });
    }
  };

  const toggleOne = (id: number) => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelection(new Set());

  const handleIgnore = async (p: PendingFacet) => {
    try {
      await adminApi.classifiers.ignorePending(p.id);
      // Optimistically drop the row when viewing the pending list — it's
      // either now hidden (status='ignored' filter is 'pending') or stays
      // as a now-ignored row (filter='all'/'ignored').
      if (filter.status === 'pending') {
        setRows((prev) => prev.filter((x) => x.id !== p.id));
      } else {
        setRows((prev) => prev.map((x) => (x.id === p.id ? { ...x, status: 'ignored' } : x)));
      }
      await onAfterMutation(`Valoare ignorată: ${p.value}`);
    } catch (e) {
      onError(extractError(e, 'Eroare la ignorarea valorii'));
    }
  };

  // Shared completion path for the three bulk modals. The modal owns the
  // network call (so it can keep itself open on a 400 with an inline
  // error) and hands us the envelope here once it succeeds.
  const handleBulkDone = useCallback(
    async (action: BulkAction, requestedIds: number[], result: BulkActionResult) => {
      setBulkResult({ action, total: requestedIds.length, result });
      clearSelection();
      // The page-level info flash gives a quick one-liner; the local
      // banner (rendered below) carries the expandable error detail list.
      const summary =
        result.skipped === 0
          ? `${result.processed}/${requestedIds.length} ${BULK_ACTION_PAST[action]}`
          : `${result.processed}/${requestedIds.length} ${BULK_ACTION_PAST[action]}, ${result.skipped} omise`;
      await load();
      await onAfterMutation(summary);
    },
    [load, onAfterMutation]
  );

  const selectionCount = selection.size;
  const mergeDisabledReason = spansMultipleClassifiers
    ? 'Selectează rânduri din același clasificator pentru merge'
    : null;
  const isMobile = useIsMobileViewport();
  const filtersActive =
    (filter.classifier_id !== 'all') || (filter.status !== 'pending');

  return (
    <>
      <details
        className="admin-toolbar-panel admin-toolbar-collapsible"
        open={!isMobile}
      >
        <summary className="admin-toolbar-summary">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="7" y1="12" x2="17" y2="12" />
            <line x1="10" y1="18" x2="14" y2="18" />
          </svg>
          <span>Filtre {filtersActive ? <em className="admin-toolbar-summary__active">(active)</em> : null}</span>
          <svg className="admin-toolbar-summary__chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </summary>
        <div className="admin-toolbar-panel__row">
          <div className="admin-toolbar-search-block">
            <span className="admin-toolbar-filters-label">Clasificator</span>
            <select
              className="admin-select"
              value={filter.classifier_id === 'all' ? '' : String(filter.classifier_id)}
              onChange={(e) =>
                onFilterChange({
                  ...filter,
                  classifier_id: e.target.value === '' ? 'all' : Number(e.target.value),
                })
              }
            >
              <option value="">Toți clasificatorii</option>
              {classifiers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label_ro}
                </option>
              ))}
            </select>
          </div>
          <div className="admin-toolbar-filters-block">
            <span className="admin-toolbar-filters-label">Stare</span>
            <div className="admin-filters" role="group" aria-label="Filtru stare">
              {(['pending', 'ignored', 'all'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`admin-filter-chip${filter.status === s ? ' active' : ''}`}
                  onClick={() => onFilterChange({ ...filter, status: s })}
                >
                  {s === 'pending' ? 'Netriajate' : s === 'ignored' ? 'Ignorate' : 'Toate'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </details>

      {selectionCount > 0 && (
        <div
          className="adm-cls-bulk-bar"
          role="region"
          aria-label="Acțiuni în masă pentru selecție"
        >
          <span className="adm-cls-bulk-bar__count">
            {selectionCount}{' '}
            {selectionCount === 1 ? 'rând selectat' : 'rânduri selectate'}
          </span>
          <div className="adm-cls-bulk-bar__actions">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setBulkPromoteOpen(true)}
            >
              Promovează toate
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setBulkMergeOpen(true)}
              disabled={mergeDisabledReason !== null}
              title={mergeDisabledReason ?? undefined}
            >
              Merge cu…
            </button>
            {mergeDisabledReason && (
              <span className="adm-cls-bulk-bar__hint" role="note">
                {mergeDisabledReason}
              </span>
            )}
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => setBulkIgnoreOpen(true)}
            >
              Ignoră toate
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm adm-cls-bulk-bar__clear"
              onClick={clearSelection}
            >
              Deselectează
            </button>
          </div>
        </div>
      )}

      {bulkResult && (
        <BulkResultBanner result={bulkResult} onDismiss={() => setBulkResult(null)} />
      )}

      <div className="admin-users-card">
        <div className="admin-users-table-wrap">
          <table className="adm-cls-pending-table">
            <thead>
              <tr>
                <th className="adm-cls-pending-table__check" scope="col">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    className="adm-cls-bulk-check"
                    aria-label="Selectează toate rândurile vizibile"
                    checked={allVisibleSelected}
                    disabled={rows.length === 0}
                    onChange={toggleAllVisible}
                  />
                </th>
                <th>Clasificator</th>
                <th>Valoare</th>
                <th className="adm-cls-pending-table__num">Apariții</th>
                <th>Prima vedere</th>
                <th>Ultima vedere</th>
                <th>Stare</th>
                <th className="admin-users-th-actions" aria-label="Acțiuni" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="adm-cls-empty">
                    Se încarcă…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="adm-cls-empty">
                    Nicio valoare netriajată pentru filtrul curent.
                  </td>
                </tr>
              ) : (
                rows.map((p) => {
                  const isChecked = selection.has(p.id);
                  return (
                    <tr key={p.id} className={isChecked ? 'adm-cls-pending-row--selected' : undefined}>
                      <td className="adm-cls-pending-table__check">
                        <input
                          type="checkbox"
                          className="adm-cls-bulk-check"
                          aria-label={`Selectează valoarea ${p.value}`}
                          checked={isChecked}
                          onChange={() => toggleOne(p.id)}
                        />
                      </td>
                      <td>{classifierLabel(p.classifier_id)}</td>
                      <td>
                        <span className="adm-cls-pending-value">{p.value}</span>
                      </td>
                      <td className="adm-cls-pending-table__num">{p.occurrences_count}</td>
                      <td>{new Date(p.first_seen_at).toLocaleDateString('ro-RO')}</td>
                      <td>{new Date(p.last_seen_at).toLocaleDateString('ro-RO')}</td>
                      <td>
                        {p.status === 'ignored' ? (
                          <span className="adm-cls-pending-status--ignored">Ignorată</span>
                        ) : (
                          'Netriajată'
                        )}
                      </td>
                      <td className="admin-users-cell-actions">
                        <RowActionsMenu
                          open={openMenuId === p.id}
                          onOpen={() => setOpenMenuId(p.id)}
                          onClose={() => setOpenMenuId(null)}
                          ariaLabel={`Acțiuni valoare ${p.value}`}
                          items={[
                            {
                              label: 'Promovează',
                              onClick: () => {
                                setOpenMenuId(null);
                                setPromoteState({ pending: p });
                              },
                            },
                            {
                              label: 'Merge cu…',
                              onClick: () => {
                                setOpenMenuId(null);
                                setMergeState({ pending: p });
                              },
                            },
                            { type: 'separator' },
                            {
                              label: 'Ignoră',
                              danger: true,
                              onClick: () => {
                                setOpenMenuId(null);
                                void handleIgnore(p);
                              },
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PendingPromoteModal
        state={promoteState}
        classifiers={classifiers}
        onClose={() => setPromoteState(null)}
        onSaved={async (msg) => {
          setPromoteState(null);
          await load();
          await onAfterMutation(msg);
        }}
        onError={onError}
      />

      <PendingMergeModal
        state={mergeState}
        classifiers={classifiers}
        onClose={() => setMergeState(null)}
        onSaved={async (msg) => {
          setMergeState(null);
          await load();
          await onAfterMutation(msg);
        }}
        onError={onError}
      />

      <PendingBulkPromoteModal
        open={bulkPromoteOpen}
        rows={visibleSelectedRows}
        commonClassifierId={commonClassifierId}
        commonClassifierLabel={
          commonClassifierId !== null ? classifierLabel(commonClassifierId) : null
        }
        onClose={() => setBulkPromoteOpen(false)}
        onDone={async (result) => {
          setBulkPromoteOpen(false);
          await handleBulkDone('promote', visibleSelectedIds, result);
        }}
        onError={onError}
      />

      <PendingBulkMergeModal
        open={bulkMergeOpen}
        rows={visibleSelectedRows}
        classifierId={commonClassifierId}
        classifierLabel={
          commonClassifierId !== null ? classifierLabel(commonClassifierId) : ''
        }
        onClose={() => setBulkMergeOpen(false)}
        onDone={async (result) => {
          setBulkMergeOpen(false);
          await handleBulkDone('merge', visibleSelectedIds, result);
        }}
        onError={onError}
      />

      <PendingBulkIgnoreModal
        open={bulkIgnoreOpen}
        rows={visibleSelectedRows}
        onClose={() => setBulkIgnoreOpen(false)}
        onDone={async (result) => {
          setBulkIgnoreOpen(false);
          await handleBulkDone('ignore', visibleSelectedIds, result);
        }}
        onError={onError}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Bulk result envelope banner
//
// Rendered above the pending table after any bulk action lands. The
// page-level `info` flash gives a one-liner; this banner additionally
// surfaces the per-row failure list inside a collapsed `<details>` so the
// admin can scroll back to the offending pending IDs if needed.
// ---------------------------------------------------------------------------

const BulkResultBanner: React.FC<{ result: BulkResultState; onDismiss: () => void }> = ({
  result,
  onDismiss,
}) => {
  const past = BULK_ACTION_PAST[result.action];
  const { processed, skipped, errors } = result.result;
  const isFull = skipped === 0 && errors.length === 0;
  const headline = isFull
    ? `${processed}/${result.total} ${past}`
    : `${processed}/${result.total} ${past}, ${skipped} omise`;
  return (
    <div
      className={`admin-flash adm-cls-bulk-result ${
        isFull ? 'admin-flash--success' : 'admin-flash--info'
      }`}
      role="status"
    >
      <div className="adm-cls-bulk-result__head">
        <span className="admin-flash__text">{headline}</span>
        <button
          type="button"
          className="adm-cls-bulk-result__dismiss"
          onClick={onDismiss}
          aria-label="Închide"
        >
          ×
        </button>
      </div>
      {errors.length > 0 && (
        <details className="adm-cls-bulk-result__errors">
          <summary>
            {errors.length === 1
              ? '1 rând cu eroare'
              : `${errors.length} rânduri cu erori`}
          </summary>
          <ul className="adm-cls-bulk-result__error-list">
            {errors.map((err) => (
              <li key={err.pending_id}>
                <code>#{err.pending_id}</code> — {err.reason}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared portal-positioned kebab menu.
//
// Generalized from the AdminUsers `RowActionsMenu`: same flip-above /
// shift-left positioning, same dismiss rules (outside click, Escape,
// scroll/resize reposition), same fixed-portal so card / table
// `overflow: hidden` chrome can't clip it. The items array makes it
// reusable across classifier cards, tree nodes and pending rows.
// ---------------------------------------------------------------------------

type MenuItem =
  | { type: 'separator' }
  | { label: string; onClick: () => void; danger?: boolean; disabled?: boolean };

interface RowActionsMenuProps {
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  items: MenuItem[];
  ariaLabel: string;
}

const MENU_WIDTH = 208;
const MENU_HEIGHT_EST = 200;

const RowActionsMenu: React.FC<RowActionsMenuProps> = ({
  open,
  onOpen,
  onClose,
  items,
  ariaLabel,
}) => {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const recompute = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    let left = rect.right - MENU_WIDTH;
    let top = rect.bottom + 6;

    if (top + MENU_HEIGHT_EST > viewportH - 8) {
      top = rect.top - MENU_HEIGHT_EST - 6;
    }
    if (left < 8) left = 8;
    if (left + MENU_WIDTH > viewportW - 8) left = viewportW - MENU_WIDTH - 8;

    setPos({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    recompute();
  }, [open, recompute]);

  useEffect(() => {
    if (!open) return;

    const handleDown = (ev: MouseEvent) => {
      const target = ev.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popRef.current?.contains(target)) return;
      onClose();
    };
    const handleKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose();
    };
    const handleScroll = () => recompute();
    const handleResize = () => recompute();

    document.addEventListener('mousedown', handleDown);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleDown);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [open, onClose, recompute]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`admin-users-menu-btn${open ? ' admin-users-menu-btn--open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={(e) => {
          e.stopPropagation();
          open ? onClose() : onOpen();
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <circle cx="12" cy="5" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="12" cy="19" r="1.6" />
        </svg>
      </button>

      {open &&
        pos &&
        createPortal(
          <div
            ref={popRef}
            className="admin-users-menu-pop"
            role="menu"
            style={{ top: pos.top, left: pos.left, width: MENU_WIDTH }}
            onClick={(e) => e.stopPropagation()}
          >
            {items.map((it, i) =>
              'type' in it ? (
                <div key={`sep-${i}`} className="admin-users-menu-sep" />
              ) : (
                <button
                  key={`it-${i}-${it.label}`}
                  type="button"
                  role="menuitem"
                  disabled={it.disabled}
                  className={`admin-users-menu-item${
                    it.danger ? ' admin-users-menu-item--danger' : ''
                  }`}
                  onClick={it.onClick}
                >
                  {it.label}
                </button>
              )
            )}
          </div>,
          document.body
        )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Classifier create/edit modal
// ---------------------------------------------------------------------------

interface ClassifierFormState {
  key: string;
  label_ro: string;
  label_en: string;
  description: string;
  json_path: string;
  cardinality: ClassifierCardinality;
  value_source: ClassifierValueSource;
  show_in_filters: boolean;
  show_in_card: boolean;
  show_in_detail: boolean;
  display_order: number;
  active: boolean;
}

const emptyClassifierForm = (): ClassifierFormState => ({
  key: '',
  label_ro: '',
  label_en: '',
  description: '',
  json_path: '',
  cardinality: 'multi',
  value_source: 'mixed',
  show_in_filters: true,
  show_in_card: false,
  show_in_detail: true,
  display_order: 100,
  active: true,
});

const fromClassifier = (c: Classifier): ClassifierFormState => ({
  key: c.key,
  label_ro: c.label_ro,
  label_en: c.label_en ?? '',
  description: c.description ?? '',
  json_path: c.json_path,
  cardinality: c.cardinality,
  value_source: c.value_source,
  show_in_filters: c.show_in_filters,
  show_in_card: c.show_in_card,
  show_in_detail: c.show_in_detail,
  display_order: c.display_order,
  active: c.active,
});

interface ClassifierEditorModalProps {
  state: { mode: 'create' | 'edit'; classifier?: Classifier } | null;
  onClose: () => void;
  onSaved: (msg: string) => Promise<void> | void;
  onError: (msg: string) => void;
}

const ClassifierEditorModal: React.FC<ClassifierEditorModalProps> = ({
  state,
  onClose,
  onSaved,
  onError,
}) => {
  const formId = useId();
  const isEdit = state?.mode === 'edit';
  const [form, setForm] = useState<ClassifierFormState>(emptyClassifierForm());
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!state) return;
    setForm(state.classifier ? fromClassifier(state.classifier) : emptyClassifierForm());
    setLocalError('');
    setSaving(false);
  }, [state]);

  const set = <K extends keyof ClassifierFormState>(key: K, value: ClassifierFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const keyValid = isValidKey(form.key);
  const pathValid = isValidJsonPath(form.json_path);

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!state) return;
    setLocalError('');
    if (!form.label_ro.trim()) {
      setLocalError('Eticheta în română este obligatorie');
      return;
    }
    if (!keyValid) {
      setLocalError('Cheia trebuie să respecte formatul: literă mică, urmată de litere mici / cifre / underscore.');
      return;
    }
    if (!pathValid) {
      setLocalError(
        'Calea JSON trebuie să fie de forma "a.b.c" sau "a[].b" (segmente slug separate prin punct, cu „[]” opțional la nivel de array).'
      );
      return;
    }
    setSaving(true);
    try {
      if (isEdit && state.classifier) {
        const payload: ClassifierUpdatePayload = {
          key: form.key.trim(),
          label_ro: form.label_ro.trim(),
          label_en: form.label_en.trim() || null,
          description: form.description.trim() || null,
          json_path: form.json_path.trim(),
          cardinality: form.cardinality,
          value_source: form.value_source,
          show_in_filters: form.show_in_filters,
          show_in_card: form.show_in_card,
          show_in_detail: form.show_in_detail,
          display_order: form.display_order,
          active: form.active,
        };
        await adminApi.classifiers.update(state.classifier.id, payload);
        await onSaved(`Clasificator actualizat: ${form.label_ro.trim()}`);
      } else {
        const payload: ClassifierCreatePayload = {
          key: form.key.trim(),
          label_ro: form.label_ro.trim(),
          label_en: form.label_en.trim() || null,
          description: form.description.trim() || null,
          json_path: form.json_path.trim(),
          cardinality: form.cardinality,
          value_source: form.value_source,
          show_in_filters: form.show_in_filters,
          show_in_card: form.show_in_card,
          show_in_detail: form.show_in_detail,
          display_order: form.display_order,
          active: form.active,
        };
        await adminApi.classifiers.create(payload);
        await onSaved(`Clasificator creat: ${form.label_ro.trim()}`);
      }
    } catch (e) {
      const msg = extractError(e, 'Eroare la salvare');
      setLocalError(msg);
      onError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={state !== null}
      onClose={onClose}
      title={isEdit ? 'Editează clasificator' : 'Clasificator nou'}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Anulează
          </button>
          <button type="submit" form={formId} className="btn btn-primary" disabled={saving}>
            {saving ? 'Se salvează…' : isEdit ? 'Salvează' : 'Creează'}
          </button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit}>
        {localError && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--sp-4)' }}>
            {localError}
          </div>
        )}

        <div className="form-group">
          <label className="form-label" htmlFor="cls-label-ro">
            Etichetă (română)
          </label>
          <input
            id="cls-label-ro"
            type="text"
            className="form-input"
            required
            value={form.label_ro}
            onChange={(e) => set('label_ro', e.target.value)}
          />
        </div>

        <div className="adm-cls-form-row">
          <div className="form-group">
            <label className="form-label" htmlFor="cls-key">
              Cheie
            </label>
            <input
              id="cls-key"
              type="text"
              className="form-input adm-cls-text-mono"
              required
              placeholder="document_type"
              value={form.key}
              onChange={(e) => set('key', e.target.value)}
              autoComplete="off"
            />
            <p className="form-hint">
              Format: <code>literă_mică</code>, cifre și underscore. Folosit ca identificator stabil
              (URL / API).
            </p>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="cls-label-en">
              Etichetă (engleză)
            </label>
            <input
              id="cls-label-en"
              type="text"
              className="form-input"
              value={form.label_en}
              onChange={(e) => set('label_en', e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="cls-json-path">
            Cale JSON (în răspunsul LLM)
          </label>
          <input
            id="cls-json-path"
            type="text"
            className="form-input adm-cls-text-mono"
            required
            placeholder="document.types"
            value={form.json_path}
            onChange={(e) => set('json_path', e.target.value)}
            autoComplete="off"
          />
          <p className="form-hint">
            Formate acceptate: <code>a.b.c</code> (cale imbricată) sau <code>a[].b</code> (array de
            obiecte, valoare la <code>.b</code>). Exemple: <code>document.types</code>,{' '}
            <code>items[].kind</code>, <code>tags.primary</code>. Schimbarea acestei căi blochează
            extragerea pentru răspunsurile LLM deja salvate, până la reclasificare.
          </p>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="cls-desc">
            Descriere (hint pentru LLM)
          </label>
          <textarea
            id="cls-desc"
            className="form-input"
            rows={2}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder="Scurtă descriere a clasificatorului — ex: categoria principală a documentului"
          />
        </div>

        <div className="adm-cls-form-row">
          <div className="form-group">
            <label className="form-label" htmlFor="cls-cardinality">
              Cardinalitate
            </label>
            <select
              id="cls-cardinality"
              className="form-select"
              value={form.cardinality}
              onChange={(e) => set('cardinality', e.target.value as ClassifierCardinality)}
            >
              <option value="multi">Multi-valoare</option>
              <option value="single">Singură valoare</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="cls-source">
              Sursa valorilor
            </label>
            <select
              id="cls-source"
              className="form-select"
              value={form.value_source}
              onChange={(e) => set('value_source', e.target.value as ClassifierValueSource)}
            >
              <option value="taxonomy">Doar arbore (strict)</option>
              <option value="mixed">Mixt (arbore + netriajate)</option>
              <option value="freeform">Liber (orice valoare LLM)</option>
            </select>
          </div>
        </div>

        <div className="adm-cls-form-row">
          <div className="form-group">
            <label className="form-label" htmlFor="cls-order">
              Ordine afișare
            </label>
            <input
              id="cls-order"
              type="number"
              className="form-input"
              value={form.display_order}
              onChange={(e) => set('display_order', parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div className="form-group">
            <span className="form-label">Stare</span>
            <div className="adm-cls-form-checks">
              <label className="adm-cls-form-check">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => set('active', e.target.checked)}
                />
                <span>Activ</span>
              </label>
            </div>
          </div>
        </div>

        <div className="form-group">
          <span className="form-label">Unde apare</span>
          <div className="adm-cls-form-checks">
            <label className="adm-cls-form-check">
              <input
                type="checkbox"
                checked={form.show_in_filters}
                onChange={(e) => set('show_in_filters', e.target.checked)}
              />
              <span>În filtre (listă documente)</span>
            </label>
            <label className="adm-cls-form-check">
              <input
                type="checkbox"
                checked={form.show_in_card}
                onChange={(e) => set('show_in_card', e.target.checked)}
              />
              <span>Pe card (preview)</span>
            </label>
            <label className="adm-cls-form-check">
              <input
                type="checkbox"
                checked={form.show_in_detail}
                onChange={(e) => set('show_in_detail', e.target.checked)}
              />
              <span>În fișa documentului</span>
            </label>
          </div>
        </div>
      </form>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Taxonomy entry editor (create/edit a tree node)
// ---------------------------------------------------------------------------

interface TaxonomyEntryFormState {
  code: string;
  label_ro: string;
  label_en: string;
  description: string;
  aliases: string[];
  parent_id: number | null;
  sort_order: number;
  active: boolean;
}

const emptyEntryForm = (parentId: number | null): TaxonomyEntryFormState => ({
  code: '',
  label_ro: '',
  label_en: '',
  description: '',
  aliases: [],
  parent_id: parentId,
  sort_order: 0,
  active: true,
});

const fromEntry = (e: TaxonomyEntry): TaxonomyEntryFormState => ({
  code: e.code,
  label_ro: e.label_ro,
  label_en: e.label_en ?? '',
  description: e.description ?? '',
  aliases: [...(e.aliases || [])],
  parent_id: e.parent_id,
  sort_order: e.sort_order,
  active: e.active,
});

interface TaxonomyEntryEditorModalProps {
  classifier: Classifier | null;
  tree: TaxonomyTreeNode[];
  state: { mode: 'create' | 'edit'; entry?: TaxonomyTreeNode; parentId?: number | null } | null;
  onClose: () => void;
  onSaved: (msg: string) => Promise<void> | void;
  onError: (msg: string) => void;
  /** When the form is being used to promote a pending value, we want
   *  the parent component to handle save (and pass back the chosen
   *  fields). This callback overrides the default network save. */
  customSave?: (payload: TaxonomyEntryCreatePayload) => Promise<void>;
}

const TaxonomyEntryEditorModal: React.FC<TaxonomyEntryEditorModalProps> = ({
  classifier,
  tree,
  state,
  onClose,
  onSaved,
  onError,
  customSave,
}) => {
  const formId = useId();
  const isEdit = state?.mode === 'edit';
  const [form, setForm] = useState<TaxonomyEntryFormState>(emptyEntryForm(null));
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!state) return;
    if (state.entry) {
      setForm(fromEntry(state.entry));
    } else {
      setForm(emptyEntryForm(state.parentId ?? null));
    }
    setLocalError('');
    setSaving(false);
  }, [state]);

  const parentOptions = useMemo(() => flattenTree(tree), [tree]);

  const set = <K extends keyof TaxonomyEntryFormState>(
    key: K,
    value: TaxonomyEntryFormState[K]
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!state || !classifier) return;
    setLocalError('');
    if (!form.label_ro.trim()) {
      setLocalError('Eticheta în română este obligatorie');
      return;
    }
    if (form.code.trim() && !/^[a-z][a-z0-9_-]*$/i.test(form.code.trim())) {
      setLocalError('Codul trebuie să conțină doar litere, cifre, „-” și „_”.');
      return;
    }
    setSaving(true);
    try {
      if (isEdit && state.entry) {
        const payload: TaxonomyEntryUpdatePayload = {
          code: form.code.trim() || state.entry.code,
          label_ro: form.label_ro.trim(),
          label_en: form.label_en.trim() || null,
          description: form.description.trim() || null,
          aliases: form.aliases.filter((a) => a.trim().length > 0),
          parent_id: form.parent_id,
          sort_order: form.sort_order,
          active: form.active,
        };
        await adminApi.classifiers.updateEntry(state.entry.id, payload);
        await onSaved(`Valoare actualizată: ${form.label_ro.trim()}`);
      } else {
        const payload: TaxonomyEntryCreatePayload = {
          code: form.code.trim() || undefined!,
          label_ro: form.label_ro.trim(),
          label_en: form.label_en.trim() || null,
          description: form.description.trim() || null,
          aliases: form.aliases.filter((a) => a.trim().length > 0),
          parent_id: form.parent_id,
          sort_order: form.sort_order,
          active: form.active,
        };
        if (customSave) {
          await customSave(payload);
        } else {
          await adminApi.classifiers.createEntry(classifier.id, payload);
        }
        await onSaved(`Valoare creată: ${form.label_ro.trim()}`);
      }
    } catch (e) {
      const msg = extractError(e, 'Eroare la salvare');
      setLocalError(msg);
      onError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={state !== null}
      onClose={onClose}
      title={isEdit ? 'Editează valoare' : 'Valoare nouă'}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Anulează
          </button>
          <button type="submit" form={formId} className="btn btn-primary" disabled={saving}>
            {saving ? 'Se salvează…' : isEdit ? 'Salvează' : 'Creează'}
          </button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit}>
        {localError && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--sp-4)' }}>
            {localError}
          </div>
        )}

        <div className="form-group">
          <label className="form-label" htmlFor="tax-label-ro">
            Etichetă (română)
          </label>
          <input
            id="tax-label-ro"
            type="text"
            className="form-input"
            required
            value={form.label_ro}
            onChange={(e) => set('label_ro', e.target.value)}
          />
        </div>

        <div className="adm-cls-form-row">
          <div className="form-group">
            <label className="form-label" htmlFor="tax-code">
              Cod (opțional)
            </label>
            <input
              id="tax-code"
              type="text"
              className="form-input adm-cls-text-mono"
              value={form.code}
              onChange={(e) => set('code', e.target.value)}
              placeholder="raport_consult"
              autoComplete="off"
            />
            <p className="form-hint">Lăsat gol, backend-ul îl derivă din etichetă.</p>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="tax-label-en">
              Etichetă (engleză)
            </label>
            <input
              id="tax-label-en"
              type="text"
              className="form-input"
              value={form.label_en}
              onChange={(e) => set('label_en', e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="tax-parent">
            Părinte
          </label>
          <select
            id="tax-parent"
            className="form-select"
            value={form.parent_id == null ? '' : String(form.parent_id)}
            onChange={(e) => set('parent_id', e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">— rădăcină —</option>
            {parentOptions.map((opt) => (
              <option
                key={opt.id}
                value={opt.id}
                disabled={state?.entry?.id === opt.id}
              >
                {'— '.repeat(opt.depth)}
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="tax-desc">
            Descriere
          </label>
          <textarea
            id="tax-desc"
            className="form-input"
            rows={2}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />
        </div>

        <div className="form-group">
          <span className="form-label">Sinonime (aliasuri)</span>
          <ChipInput
            value={form.aliases}
            onChange={(v) => set('aliases', v)}
            placeholder="Apasă Enter pentru a adăuga…"
          />
          <p className="form-hint">
            Valorile LLM care se potrivesc unui alias vor fi normalizate la această etichetă.
          </p>
        </div>

        <div className="adm-cls-form-row">
          <div className="form-group">
            <label className="form-label" htmlFor="tax-sort">
              Ordine (între frați)
            </label>
            <input
              id="tax-sort"
              type="number"
              className="form-input"
              value={form.sort_order}
              onChange={(e) => set('sort_order', parseInt(e.target.value, 10) || 0)}
            />
          </div>
          <div className="form-group">
            <span className="form-label">Stare</span>
            <div className="adm-cls-form-checks">
              <label className="adm-cls-form-check">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => set('active', e.target.checked)}
                />
                <span>Activă</span>
              </label>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Chip input (used for taxonomy entry aliases). Enter / comma commits a chip;
// backspace at empty input pops the last chip.
// ---------------------------------------------------------------------------

interface ChipInputProps {
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}

const ChipInput: React.FC<ChipInputProps> = ({ value, onChange, placeholder }) => {
  const [draft, setDraft] = useState('');

  const commitDraft = () => {
    const t = draft.trim();
    if (!t) return;
    if (!value.includes(t)) onChange([...value, t]);
    setDraft('');
  };

  return (
    <div className="adm-cls-chip-input">
      {value.map((v, i) => (
        <span key={`${v}-${i}`} className="adm-cls-chip">
          {v}
          <button
            type="button"
            className="adm-cls-chip__x"
            aria-label={`Șterge ${v}`}
            onClick={() => onChange(value.filter((x) => x !== v))}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden>
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </span>
      ))}
      <input
        type="text"
        className="adm-cls-chip-input__field"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commitDraft();
          } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        onBlur={commitDraft}
        placeholder={placeholder}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Add cross-classifier link modal
// ---------------------------------------------------------------------------

interface LinkAddModalProps {
  open: boolean;
  sourceEntryId: number | null;
  classifiers: Classifier[];
  excludeClassifierId: number | null;
  onClose: () => void;
  onSaved: (msg: string) => Promise<void> | void;
  onError: (msg: string) => void;
}

const LinkAddModal: React.FC<LinkAddModalProps> = ({
  open,
  sourceEntryId,
  classifiers,
  excludeClassifierId,
  onClose,
  onSaved,
  onError,
}) => {
  const formId = useId();
  const [targetClassifierId, setTargetClassifierId] = useState<number | null>(null);
  const [targetEntryId, setTargetEntryId] = useState<number | null>(null);
  const [linkType, setLinkType] = useState<TaxonomyLinkType>('implies');
  const [tree, setTree] = useState<TaxonomyTreeNode[]>([]);
  const [treeLoading, setTreeLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!open) return;
    setTargetClassifierId(null);
    setTargetEntryId(null);
    setLinkType('implies');
    setTree([]);
    setLocalError('');
    setSaving(false);
  }, [open]);

  useEffect(() => {
    if (!open || targetClassifierId == null) {
      setTree([]);
      return;
    }
    setTreeLoading(true);
    adminApi.classifiers
      .getTree(targetClassifierId)
      .then(setTree)
      .catch((e) => onError(extractError(e, 'Eroare la încărcarea arborelui țintă')))
      .finally(() => setTreeLoading(false));
  }, [open, targetClassifierId, onError]);

  const options = useMemo(() => flattenTree(tree), [tree]);

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (sourceEntryId == null) return;
    setLocalError('');
    if (targetEntryId == null) {
      setLocalError('Selectează valoarea țintă.');
      return;
    }
    setSaving(true);
    try {
      await adminApi.classifiers.addLink(sourceEntryId, {
        to_entry_id: targetEntryId,
        link_type: linkType,
      });
      await onSaved('Legătură creată');
    } catch (e) {
      const msg = extractError(e, 'Eroare la crearea legăturii');
      setLocalError(msg);
      onError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Adaugă legătură"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Anulează
          </button>
          <button type="submit" form={formId} className="btn btn-primary" disabled={saving}>
            {saving ? 'Se salvează…' : 'Adaugă'}
          </button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit}>
        {localError && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--sp-4)' }}>
            {localError}
          </div>
        )}

        <div className="form-group">
          <label className="form-label" htmlFor="link-target-cls">
            Clasificator țintă
          </label>
          <select
            id="link-target-cls"
            className="form-select"
            value={targetClassifierId == null ? '' : String(targetClassifierId)}
            onChange={(e) => {
              setTargetClassifierId(e.target.value === '' ? null : Number(e.target.value));
              setTargetEntryId(null);
            }}
          >
            <option value="">—</option>
            {classifiers
              .filter((c) => c.id !== excludeClassifierId)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label_ro}
                </option>
              ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="link-target-entry">
            Valoare țintă
          </label>
          <select
            id="link-target-entry"
            className="form-select"
            value={targetEntryId == null ? '' : String(targetEntryId)}
            onChange={(e) => setTargetEntryId(e.target.value === '' ? null : Number(e.target.value))}
            disabled={targetClassifierId == null || treeLoading}
          >
            <option value="">{treeLoading ? 'Se încarcă…' : '—'}</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {'— '.repeat(o.depth)}
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="link-type">
            Tip legătură
          </label>
          <select
            id="link-type"
            className="form-select"
            value={linkType}
            onChange={(e) => setLinkType(e.target.value as TaxonomyLinkType)}
          >
            <option value="implies">implies</option>
            <option value="related">related</option>
            <option value="narrower">narrower</option>
          </select>
        </div>
      </form>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Pending value promote modal — uses TaxonomyEntryEditorModal as the form
// but routes save through /classifiers/pending/{id}/promote.
// ---------------------------------------------------------------------------

interface PendingPromoteModalProps {
  state: { pending: PendingFacet } | null;
  classifiers: Classifier[];
  onClose: () => void;
  onSaved: (msg: string) => Promise<void> | void;
  onError: (msg: string) => void;
}

const PendingPromoteModal: React.FC<PendingPromoteModalProps> = ({
  state,
  classifiers,
  onClose,
  onSaved,
  onError,
}) => {
  const formId = useId();
  const [tree, setTree] = useState<TaxonomyTreeNode[]>([]);
  const [parentId, setParentId] = useState<number | null>(null);
  const [labelRo, setLabelRo] = useState('');
  const [code, setCode] = useState('');
  const [labelEn, setLabelEn] = useState('');
  const [description, setDescription] = useState('');
  const [aliases, setAliases] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!state) return;
    setLabelRo(state.pending.value);
    setCode('');
    setLabelEn('');
    setDescription('');
    // Pre-fill the raw value as an alias so future LLM emissions of the
    // exact same string lookup_canonical-match without admin help.
    setAliases([state.pending.value]);
    setParentId(null);
    setSaving(false);
    setLocalError('');
    adminApi.classifiers
      .getTree(state.pending.classifier_id)
      .then(setTree)
      .catch((e) => onError(extractError(e, 'Eroare la încărcarea arborelui')));
  }, [state, onError]);

  const parentOptions = useMemo(() => flattenTree(tree), [tree]);
  const classifierLabel =
    classifiers.find((c) => c.id === state?.pending.classifier_id)?.label_ro || '';

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!state) return;
    setLocalError('');
    if (!labelRo.trim()) {
      setLocalError('Eticheta în română este obligatorie');
      return;
    }
    setSaving(true);
    try {
      const payload: PendingFacetPromotePayload = {
        parent_id: parentId,
        code: code.trim() || undefined,
        label_ro: labelRo.trim(),
        label_en: labelEn.trim() || null,
        description: description.trim() || null,
        aliases: aliases.filter((a) => a.trim().length > 0),
      };
      await adminApi.classifiers.promotePending(state.pending.id, payload);
      await onSaved(`Valoare promovată: ${labelRo.trim()}`);
    } catch (e) {
      const msg = extractError(e, 'Eroare la promovare');
      setLocalError(msg);
      onError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={state !== null}
      onClose={onClose}
      title={`Promovează → ${classifierLabel}`}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Anulează
          </button>
          <button type="submit" form={formId} className="btn btn-primary" disabled={saving}>
            {saving ? 'Se salvează…' : 'Promovează'}
          </button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit}>
        {localError && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--sp-4)' }}>
            {localError}
          </div>
        )}

        <p className="form-hint" style={{ marginBottom: 'var(--sp-4)' }}>
          Valoarea brută LLM: <span className="adm-cls-pending-value">{state?.pending.value}</span>
        </p>

        <div className="form-group">
          <label className="form-label" htmlFor="prm-label-ro">
            Etichetă (română)
          </label>
          <input
            id="prm-label-ro"
            type="text"
            className="form-input"
            required
            value={labelRo}
            onChange={(e) => setLabelRo(e.target.value)}
          />
        </div>

        <div className="adm-cls-form-row">
          <div className="form-group">
            <label className="form-label" htmlFor="prm-code">
              Cod (opțional)
            </label>
            <input
              id="prm-code"
              type="text"
              className="form-input adm-cls-text-mono"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="prm-label-en">
              Etichetă (engleză)
            </label>
            <input
              id="prm-label-en"
              type="text"
              className="form-input"
              value={labelEn}
              onChange={(e) => setLabelEn(e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="prm-parent">
            Părinte
          </label>
          <select
            id="prm-parent"
            className="form-select"
            value={parentId == null ? '' : String(parentId)}
            onChange={(e) => setParentId(e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">— rădăcină —</option>
            {parentOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {'— '.repeat(opt.depth)}
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="prm-desc">
            Descriere
          </label>
          <textarea
            id="prm-desc"
            className="form-input"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="form-group">
          <span className="form-label">Sinonime (aliasuri)</span>
          <ChipInput
            value={aliases}
            onChange={setAliases}
            placeholder="Apasă Enter pentru a adăuga…"
          />
        </div>
      </form>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Pending value merge modal — pick a target entry on the same classifier.
// ---------------------------------------------------------------------------

interface PendingMergeModalProps {
  state: { pending: PendingFacet } | null;
  classifiers: Classifier[];
  onClose: () => void;
  onSaved: (msg: string) => Promise<void> | void;
  onError: (msg: string) => void;
}

const PendingMergeModal: React.FC<PendingMergeModalProps> = ({
  state,
  classifiers,
  onClose,
  onSaved,
  onError,
}) => {
  const formId = useId();
  const [tree, setTree] = useState<TaxonomyTreeNode[]>([]);
  const [targetEntryId, setTargetEntryId] = useState<number | null>(null);
  const [addAsAlias, setAddAsAlias] = useState(true);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!state) return;
    setTargetEntryId(null);
    setAddAsAlias(true);
    setSaving(false);
    setLocalError('');
    adminApi.classifiers
      .getTree(state.pending.classifier_id)
      .then(setTree)
      .catch((e) => onError(extractError(e, 'Eroare la încărcarea arborelui')));
  }, [state, onError]);

  const options = useMemo(() => flattenTree(tree), [tree]);
  const classifierLabel =
    classifiers.find((c) => c.id === state?.pending.classifier_id)?.label_ro || '';

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!state) return;
    setLocalError('');
    if (targetEntryId == null) {
      setLocalError('Selectează valoarea țintă.');
      return;
    }
    setSaving(true);
    try {
      const res = await adminApi.classifiers.mergePending(state.pending.id, {
        target_entry_id: targetEntryId,
        add_as_alias: addAsAlias,
      });
      const targetLabel = labelFor(targetEntryId, tree);
      await onSaved(
        `Valoare „${state.pending.value}” fuzionată cu „${targetLabel}” (${res.updated_facets} etichete documente actualizate).`
      );
    } catch (e) {
      const msg = extractError(e, 'Eroare la fuzionare');
      setLocalError(msg);
      onError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={state !== null}
      onClose={onClose}
      title={`Fuzionează → ${classifierLabel}`}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Anulează
          </button>
          <button type="submit" form={formId} className="btn btn-primary" disabled={saving}>
            {saving ? 'Se salvează…' : 'Fuzionează'}
          </button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit}>
        {localError && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--sp-4)' }}>
            {localError}
          </div>
        )}

        <p className="form-hint" style={{ marginBottom: 'var(--sp-4)' }}>
          Valoarea brută LLM: <span className="adm-cls-pending-value">{state?.pending.value}</span>
        </p>

        <div className="form-group">
          <label className="form-label" htmlFor="mrg-target">
            Valoare canonică
          </label>
          <select
            id="mrg-target"
            className="form-select"
            value={targetEntryId == null ? '' : String(targetEntryId)}
            onChange={(e) => setTargetEntryId(e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">—</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {'— '.repeat(o.depth)}
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <div className="adm-cls-form-checks">
            <label className="adm-cls-form-check">
              <input
                type="checkbox"
                checked={addAsAlias}
                onChange={(e) => setAddAsAlias(e.target.checked)}
              />
              <span>Adaugă valoarea brută ca alias pe valoarea canonică</span>
            </label>
          </div>
        </div>
      </form>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Bulk Promote modal
//
// No label/code fields — those are derived server-side via slugify per
// row (the plan explicitly defers per-row editing here). The only knob
// is an optional shared `parent_id`, and that's only meaningful when the
// selection is single-classifier; if the admin selected rows across
// multiple classifiers we hide the parent picker and force `parent_id:
// null` on the wire.
// ---------------------------------------------------------------------------

interface PendingBulkPromoteModalProps {
  open: boolean;
  rows: PendingFacet[];
  commonClassifierId: number | null;
  commonClassifierLabel: string | null;
  onClose: () => void;
  onDone: (result: BulkActionResult) => Promise<void> | void;
  onError: (msg: string) => void;
}

const PendingBulkPromoteModal: React.FC<PendingBulkPromoteModalProps> = ({
  open,
  rows,
  commonClassifierId,
  commonClassifierLabel,
  onClose,
  onDone,
  onError,
}) => {
  const formId = useId();
  const [tree, setTree] = useState<TaxonomyTreeNode[]>([]);
  const [parentId, setParentId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!open) return;
    setParentId(null);
    setSaving(false);
    setLocalError('');
    setTree([]);
    if (commonClassifierId !== null) {
      adminApi.classifiers
        .getTree(commonClassifierId)
        .then(setTree)
        .catch((e) => onError(extractError(e, 'Eroare la încărcarea arborelui')));
    }
  }, [open, commonClassifierId, onError]);

  const parentOptions = useMemo(() => flattenTree(tree), [tree]);

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (rows.length === 0) return;
    setLocalError('');
    setSaving(true);
    try {
      const ids = rows.map((r) => r.id);
      const res = await adminApi.classifiers.bulkPromotePending(ids, parentId);
      await onDone(res);
    } catch (e) {
      const msg = extractError(e, 'Eroare la promovarea în masă');
      setLocalError(msg);
      onError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Promovează ${rows.length} valori`}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Anulează
          </button>
          <button type="submit" form={formId} className="btn btn-primary" disabled={saving || rows.length === 0}>
            {saving ? 'Se salvează…' : `Promovează ${rows.length}`}
          </button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit}>
        {localError && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--sp-4)' }}>
            {localError}
          </div>
        )}

        <p className="form-hint" style={{ marginBottom: 'var(--sp-3)' }}>
          Următoarele {rows.length} valori vor fi promovate. Codurile sunt generate
          automat din etichetă (slugify); eticheta în română este valoarea brută.
        </p>

        <div className="adm-cls-bulk-preview">
          {rows.slice(0, 12).map((r) => (
            <span key={r.id} className="adm-cls-pending-value adm-cls-bulk-preview__chip">
              {r.value}
            </span>
          ))}
          {rows.length > 12 && (
            <span className="adm-cls-bulk-preview__more">
              +{rows.length - 12} altele
            </span>
          )}
        </div>

        {commonClassifierId !== null ? (
          <div className="form-group" style={{ marginTop: 'var(--sp-4)' }}>
            <label className="form-label" htmlFor="bulk-prm-parent">
              Părinte comun (opțional) — {commonClassifierLabel}
            </label>
            <select
              id="bulk-prm-parent"
              className="form-select"
              value={parentId == null ? '' : String(parentId)}
              onChange={(e) => setParentId(e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">— rădăcină —</option>
              {parentOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {'— '.repeat(opt.depth)}
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <p className="form-hint" style={{ marginTop: 'var(--sp-4)' }}>
            Selecția conține mai mulți clasificatori — toate valorile vor fi
            promovate fără un părinte comun.
          </p>
        )}
      </form>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Bulk Merge modal
//
// Single classifier only (the toolbar prevents opening this in the
// cross-classifier case). Target picker is the same flat tree as the
// single-row merge modal; `add_as_alias` defaults to true to match the
// per-row default. Backend still validates target.classifier_id matches
// the pending rows' classifier and 400s on mismatch — we surface the
// `detail` from that response inline if it ever happens.
// ---------------------------------------------------------------------------

interface PendingBulkMergeModalProps {
  open: boolean;
  rows: PendingFacet[];
  classifierId: number | null;
  classifierLabel: string;
  onClose: () => void;
  onDone: (result: BulkActionResult) => Promise<void> | void;
  onError: (msg: string) => void;
}

const PendingBulkMergeModal: React.FC<PendingBulkMergeModalProps> = ({
  open,
  rows,
  classifierId,
  classifierLabel,
  onClose,
  onDone,
  onError,
}) => {
  const formId = useId();
  const [tree, setTree] = useState<TaxonomyTreeNode[]>([]);
  const [targetEntryId, setTargetEntryId] = useState<number | null>(null);
  const [addAsAlias, setAddAsAlias] = useState(true);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!open) return;
    setTargetEntryId(null);
    setAddAsAlias(true);
    setSaving(false);
    setLocalError('');
    setTree([]);
    if (classifierId !== null) {
      adminApi.classifiers
        .getTree(classifierId)
        .then(setTree)
        .catch((e) => onError(extractError(e, 'Eroare la încărcarea arborelui')));
    }
  }, [open, classifierId, onError]);

  const options = useMemo(() => flattenTree(tree), [tree]);

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (rows.length === 0) return;
    setLocalError('');
    if (targetEntryId == null) {
      setLocalError('Selectează valoarea țintă.');
      return;
    }
    setSaving(true);
    try {
      const ids = rows.map((r) => r.id);
      const res = await adminApi.classifiers.bulkMergePending(ids, targetEntryId, addAsAlias);
      await onDone(res);
    } catch (e) {
      const msg = extractError(e, 'Eroare la fuzionarea în masă');
      setLocalError(msg);
      onError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Fuzionează ${rows.length} valori → ${classifierLabel}`}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Anulează
          </button>
          <button type="submit" form={formId} className="btn btn-primary" disabled={saving || rows.length === 0}>
            {saving ? 'Se salvează…' : `Fuzionează ${rows.length}`}
          </button>
        </>
      }
    >
      <form id={formId} onSubmit={handleSubmit}>
        {localError && (
          <div className="alert alert-error" style={{ marginBottom: 'var(--sp-4)' }}>
            {localError}
          </div>
        )}

        <p className="form-hint" style={{ marginBottom: 'var(--sp-3)' }}>
          Următoarele {rows.length} valori vor fi fuzionate într-o singură
          valoare canonică din clasificatorul „{classifierLabel}”.
        </p>

        <div className="adm-cls-bulk-preview">
          {rows.slice(0, 12).map((r) => (
            <span key={r.id} className="adm-cls-pending-value adm-cls-bulk-preview__chip">
              {r.value}
            </span>
          ))}
          {rows.length > 12 && (
            <span className="adm-cls-bulk-preview__more">
              +{rows.length - 12} altele
            </span>
          )}
        </div>

        <div className="form-group" style={{ marginTop: 'var(--sp-4)' }}>
          <label className="form-label" htmlFor="bulk-mrg-target">
            Valoare canonică
          </label>
          <select
            id="bulk-mrg-target"
            className="form-select"
            value={targetEntryId == null ? '' : String(targetEntryId)}
            onChange={(e) => setTargetEntryId(e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">—</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {'— '.repeat(o.depth)}
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <div className="adm-cls-form-checks">
            <label className="adm-cls-form-check">
              <input
                type="checkbox"
                checked={addAsAlias}
                onChange={(e) => setAddAsAlias(e.target.checked)}
              />
              <span>Adaugă valorile brute ca alias pe valoarea canonică</span>
            </label>
          </div>
        </div>
      </form>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Bulk Ignore modal — confirm-only, no form fields.
// ---------------------------------------------------------------------------

interface PendingBulkIgnoreModalProps {
  open: boolean;
  rows: PendingFacet[];
  onClose: () => void;
  onDone: (result: BulkActionResult) => Promise<void> | void;
  onError: (msg: string) => void;
}

const PendingBulkIgnoreModal: React.FC<PendingBulkIgnoreModalProps> = ({
  open,
  rows,
  onClose,
  onDone,
  onError,
}) => {
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (!open) return;
    setSaving(false);
    setLocalError('');
  }, [open]);

  const handleConfirm = async () => {
    if (rows.length === 0) return;
    setLocalError('');
    setSaving(true);
    try {
      const ids = rows.map((r) => r.id);
      const res = await adminApi.classifiers.bulkIgnorePending(ids);
      await onDone(res);
    } catch (e) {
      const msg = extractError(e, 'Eroare la ignorarea în masă');
      setLocalError(msg);
      onError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Ignoră în masă"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Anulează
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => void handleConfirm()}
            disabled={saving || rows.length === 0}
          >
            {saving ? 'Se procesează…' : `Ignoră ${rows.length}`}
          </button>
        </>
      }
    >
      {localError && (
        <div className="alert alert-error" style={{ marginBottom: 'var(--sp-4)' }}>
          {localError}
        </div>
      )}
      <p>
        Sigur vrei să ignori{' '}
        <strong>
          {rows.length} {rows.length === 1 ? 'rând' : 'rânduri'}
        </strong>
        ? Valorile vor fi marcate ca <em>ignorate</em> și nu vor mai apărea în
        lista netriajate. Acțiunea poate fi inversată din filtrul „Ignorate”.
      </p>
      <div className="adm-cls-bulk-preview" style={{ marginTop: 'var(--sp-3)' }}>
        {rows.slice(0, 12).map((r) => (
          <span key={r.id} className="adm-cls-pending-value adm-cls-bulk-preview__chip">
            {r.value}
          </span>
        ))}
        {rows.length > 12 && (
          <span className="adm-cls-bulk-preview__more">+{rows.length - 12} altele</span>
        )}
      </div>
    </Modal>
  );
};

export default AdminClassifications;
